import { i18nLike } from "#web/services/i18nLike";
import { Badge, BlockStack, Box, Button, Card, Icon, IconSource, InlineStack, Text } from "@shopify/polaris";
import { CheckSmallIcon, XSmallIcon } from "@shopify/polaris-icons";

const en = {
  currency: "$",
  mostPopular: "Most popular",
  perMonth: "per month",
  shopifyPlusRequired: "Shopify Plus required",
  trialPeriod: "14-day free trial",
  tryForFree: "Try for free",
};

interface PlanCardProps {
  badge?: string;
  crossedFeatures?: string[];
  description: string;
  disabled?: boolean;
  featured?: boolean;
  features: string[];
  icon: IconSource;
  loading?: boolean;
  onAction: () => void;
  price: string;
  title: string;
}

export default function PlanCard({
  badge,
  crossedFeatures,
  description,
  disabled = false,
  featured = false,
  features,
  icon,
  loading,
  onAction,
  price,
  title,
}: PlanCardProps) {
  const t = i18nLike(en);

  return (
    <Card>
      <BlockStack gap="100" inlineAlign="start">
        <Box paddingBlockEnd="400">
          {!disabled && featured ? (
            <Badge tone="magic">
              {/* @ts-expect-error Element is not assignable to string */}
              <InlineStack gap="100">
                <Icon source={icon} tone="magic" />
                <Text as="span" tone="magic" variant="bodyMd">
                  {t("mostPopular")}
                </Text>
              </InlineStack>
            </Badge>
          ) : (
            <Icon source={icon} tone="primary" />
          )}
        </Box>
        <Text as="h6" variant="headingMd">
          {title}
        </Text>
        <Box paddingBlockEnd="400">
          <Text as="span" tone="subdued" variant="bodyMd">
            {description}
          </Text>
        </Box>
        <InlineStack blockAlign="baseline" gap="200">
          <Text as="h4" variant="heading2xl">
            {t("currency")}
            {price}
          </Text>
          <Text as="span" tone="subdued" variant="bodyMd">
            {t("perMonth")}
          </Text>
        </InlineStack>
        <Box minWidth="100%" paddingBlock="800">
          <BlockStack gap="200">
            <Button
              disabled={disabled}
              fullWidth
              loading={loading}
              onClick={onAction}
              size="large"
              textAlign="center"
              variant={!disabled && featured ? "primary" : "secondary"}
            >
              {disabled ? t("shopifyPlusRequired") : t("tryForFree")}
            </Button>
            <Text alignment="center" as="span" tone="subdued" variant="bodySm">
              {t("trialPeriod")}
            </Text>
          </BlockStack>
        </Box>
        <Box minHeight="170">
          <BlockStack gap="200">
            {features.map((feature, index) => (
              <InlineStack blockAlign="start" gap="400" key={index} wrap={false}>
                <div>
                  <Icon source={CheckSmallIcon} tone="info"></Icon>
                </div>
                <Text as="span" tone="base" variant="bodyMd">
                  {feature}
                </Text>
              </InlineStack>
            ))}
            {crossedFeatures?.map((feature, index) => (
              <InlineStack blockAlign="start" gap="400" key={index} wrap={false}>
                <div>
                  <Icon source={XSmallIcon} tone="critical"></Icon>
                </div>
                <Text as="span" tone="base" variant="bodyMd">
                  {feature}
                </Text>
              </InlineStack>
            ))}
          </BlockStack>
          <Box paddingBlockStart={"400"}>
            {badge && (
              <Badge>
                {/* @ts-expect-error Element is not assignable to string */}
                <Box paddingBlock={"100"}>
                  <Text as="span" tone="base" variant="bodySm">
                    {badge}
                  </Text>
                </Box>
              </Badge>
            )}
          </Box>
        </Box>
      </BlockStack>
    </Card>
  );
}
